import { ReactNode, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import Text from "@/components/Text";
import { REQUIRED_SELECTION_MESSAGE } from "@/constants/validation-constant";
import SectionAccordion from "./SectionAccordion";
import { useSectionRequirement } from "../section-requirement-context";
import OptionGroupItem from "./OptionGroupItem";
import SectionGroupHeading from "./SectionGroupHeading";
import {
  ChooseOneValue,
  GroupValue,
  OptionGroupDef,
  OptionStore,
  SummaryLine,
} from "../readmission-f2f-types";
import { buildOptionGroupDisplayEntries } from "../readmission-f2f-summary-utils";

export interface OptionGroupsSectionProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  groups: OptionGroupDef[];
  store: OptionStore;
  onChange: (store: OptionStore) => void;
  hint?: string;
  header?: ReactNode;
  footer?: ReactNode;
  extraSummaryLines?: SummaryLine[];
}

const OptionGroupsSection = ({
  title,
  expanded,
  onToggle,
  groups,
  store,
  onChange,
  hint,
  header,
  footer,
  extraSummaryLines = [],
}: OptionGroupsSectionProps) => {
  const { isRequired, isMissing } = useSectionRequirement();
  const hasAnySelection = groups.some((group) => {
    const selected = store[group.key]?.selected;
    return Array.isArray(selected) ? selected.length > 0 : !!selected;
  });
  // Shown only when the last Save & Sign flagged the section and no option is ticked anywhere in it.
  const selectionError =
    isRequired && isMissing && !hasAnySelection
      ? REQUIRED_SELECTION_MESSAGE
      : undefined;
  const summaryLines = [
    ...extraSummaryLines,
    ...groups.flatMap((group) =>
      buildOptionGroupDisplayEntries(group, store[group.key])
    ),
  ];

  // Read through refs so `setGroup` stays stable and memoised items skip unrelated changes.
  const storeRef = useRef(store);
  storeRef.current = store;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const setGroup = useCallback(
    (key: string, value: GroupValue | ChooseOneValue) =>
      onChangeRef.current({ ...storeRef.current, [key]: value }),
    []
  );

  // Consecutive groups with the same heading share one bordered box.
  const groupRuns = groups.reduce<
    { heading?: string; groups: OptionGroupDef[] }[]
  >((runs, group) => {
    const last = runs[runs.length - 1];
    if (last && last.heading === group.heading && group.heading) {
      last.groups.push(group);
    } else {
      runs.push({ heading: group.heading, groups: [group] });
    }
    return runs;
  }, []);

  return (
    <SectionAccordion
      title={title}
      expanded={expanded}
      onToggle={onToggle}
      summaryLines={summaryLines}
      unmountWhenCollapsed
    >
      {hint && (
        <Box sx={{ bgcolor: "#1A1A1A0D", borderRadius: 1, p: 1 }}>
          <Text size={13} c="text.secondary">
            {hint}
          </Text>
        </Box>
      )}
      {header}
      {selectionError && (
        <Text size={12} c="error.main">
          {selectionError}
        </Text>
      )}
      {groupRuns.map((run) => {
        const items = run.groups.map((group) => (
          <OptionGroupItem
            key={group.key}
            group={group}
            value={store[group.key]}
            onChange={setGroup}
            bordered={!run.heading}
          />
        ));
        if (!run.heading) return items;
        return (
          <Box
            key={run.heading}
            sx={{ border: "1px solid #E0E0E0", borderRadius: 1, p: 1.5 }}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <SectionGroupHeading sx={{ mt: 0 }}>
              {run.heading}
            </SectionGroupHeading>
            {items}
          </Box>
        );
      })}
      {footer}
    </SectionAccordion>
  );
};

export default OptionGroupsSection;
